/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import get from 'lodash/get';
import classNames from 'classnames';

import Icon from 'carbon-react/lib/components/icon';

import { toastError } from '@futrli/ui-components';
import { FormattedMessage } from '@futrli/localisation';
import { Logger, REQUEST_METHODS } from '@futrli/shared-services';

import { Data } from '../../../utils/table-filters/filter-table-types';
import { SendEmailDialog } from './sent-email-dialog';
import { EXPORTED_PDF_STATUS } from '../../../report-builder/components/document-library/exported-pdfs/exported-pdfs';
import { DeleteScheduleReportDialog } from './delete-schedule-report-dialog';
import { deleteExportedReport } from '../../../core/api/reporting/report-document-export';
import { makeRequestWithResponse } from '../../../core/api/shared';
import { downloadFile, viewFile } from './utils';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';

import * as styles from '../virtualized-list.scss';

interface Props {
  rowData?: Data;
  rowIndex: number;
  revalidateData?: () => Promise<void>;
}

export const ExportedPdfsTableRow = ({ rowData, rowIndex, revalidateData }: Props) => {
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { isConsolidation } = usePredictionsInfoContext();

  const disableActions = rowData?.status === EXPORTED_PDF_STATUS.PENDING_RENDERING;

  const onDownloadClick = async () => {
    try {
      setIsDownloading(true);
      const downloadLink = get(rowData, 'download_link', '');
      if (downloadLink && rowData?.name) {
        const downloadURL: { url: string } = await makeRequestWithResponse({
          url: downloadLink,
          method: REQUEST_METHODS.GET
        });
        await downloadFile(downloadURL.url, `${rowData.name}.pdf`);
      }
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsDownloading(false);
    }
  };

  const onViewClick = async () => {
    try {
      setIsViewing(true);
      const downloadLink = get(rowData, 'download_link', '');
      if (downloadLink && rowData?.name) {
        const downloadURL: { view_url: string } = await makeRequestWithResponse({
          url: downloadLink,
          method: REQUEST_METHODS.GET
        });
        await viewFile(downloadURL.view_url);
      }
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsViewing(false);
    }
  };

  const handleOpenEmailDialog = () => {
    setShowEmailDialog(true);
  };

  const handleCloseEmailDialog = () => {
    setShowEmailDialog(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (rowData?.id) {
      try {
        setIsLoading(true);
        await deleteExportedReport(workspaceId, organisationId, rowData.id, isConsolidation);
        revalidateData?.();
        handleCloseDeleteDialog();
      } catch (e) {
        Logger.error(e as string);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!rowData) {
    return null;
  }

  return (
    <div
      role="row"
      key={rowData.id}
      className={clsx(styles.virtualizedListRow)}
      style={{ backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : 'transparent', position: 'relative' }}
    >
      <div
        role="cell"
        className={clsx(styles.virtualizedListCell, styles.flex6, styles.documentLibraryTableFont, styles.svgContainer)}
        style={{ minWidth: 0, border: 'none' }}
      >
        <Icon type="file_pdf" />
        <span className={styles.textOverflow}>{`${rowData.name}.pdf`}</span>
      </div>
      <div
        role="cell"
        className={clsx(styles.virtualizedListCell, styles.flex2, styles.documentLibraryTableFont)}
        style={{ minWidth: 0, border: 'none' }}
      >
        <span className={styles.textOverflow}>
          {rowData.status === EXPORTED_PDF_STATUS.PENDING_RENDERING ? 'Pending' : ''}
        </span>
      </div>
      <div
        role="cell"
        className={clsx(styles.virtualizedListCell, styles.flex2, styles.documentLibraryTableFont)}
        style={{ minWidth: 0, border: 'none' }}
      >
        <span className={styles.textOverflow}>{rowData.created_by}</span>
      </div>
      {rowData.saved_on ? (
        <div
          role="cell"
          className={clsx(styles.virtualizedListCell, styles.flex2, styles.documentLibraryTableFont)}
          style={{ minWidth: 0, border: 'none' }}
        >
          <span className={styles.saved_on}>{format(rowData.saved_on, 'dd/MM/yyyy')}</span>
        </div>
      ) : null}
      <div
        role="cell"
        className={clsx(styles.virtualizedListCell, styles.flex1, styles.documentLibraryTableFont)}
        style={{
          position: 'absolute',
          right: 0,
          display: 'flex',
          backgroundColor: '#f6f7fa',
          justifyContent: 'center',
          paddingRight: 0
        }}
      >
        <div
          className={classNames(styles.iconContainer, {
            [styles.disabledIcon]: disableActions
          })}
          onClick={handleOpenEmailDialog}
          style={{ marginRight: 12 }}
        >
          <Icon type="email" />
        </div>
        <div
          className={classNames(styles.iconContainer, {
            [styles.disabledIcon]: disableActions
          })}
          onClick={handleOpenDeleteDialog}
          style={{ marginRight: 12 }}
        >
          <Icon type="bin" />
        </div>
        <div
          className={classNames(styles.iconContainer, {
            [styles.disabledIcon]: isViewing || disableActions
          })}
          onClick={onViewClick}
          style={{ marginRight: 12 }}
        >
          <Icon type="view" />
        </div>
        <div
          className={classNames(styles.iconContainer, {
            [styles.disabledIcon]: isDownloading || disableActions
          })}
          onClick={onDownloadClick}
        >
          <Icon type="download" />
        </div>
      </div>

      {showEmailDialog && (
        <SendEmailDialog
          isOpen={showEmailDialog}
          handleClose={handleCloseEmailDialog}
          reportName={rowData.name}
          exportedReportId={rowData.id}
        />
      )}

      {showDeleteDialog && (
        <DeleteScheduleReportDialog
          isOpen={showDeleteDialog}
          handleCloseDialog={handleCloseDeleteDialog}
          onSubmit={handleDelete}
          isLoading={isLoading}
          message={
            <FormattedMessage
              id="predict.are_you_sure_delete_exported_pdf_report"
              defaultMessage="Are you sure you want to delete this exported PDF report?"
            />
          }
        />
      )}
    </div>
  );
};
